import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import BaseModal from 'components/BaseModal';
import { useRouter } from 'next/router';
import CustomButton from 'components/CustomButton';
import { convertCreation, downloadCreation } from 'utils/creationOperations';
import LimitationModal from 'components/MyCreations/LimitationModal';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  creation: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
};

export default function OldCreationVersionModal({ open, onClose, creation }) {
  const { user } = useAuthUser();
  const router = useRouter();
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingConvert, setLoadingConvert] = useState(false);
  const [oldCreationError, setOldCreationError] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [oferPlanModalOpen, setOferPlanModalOpen] = useState(false);

  function showErrorMessage() {
    setOldCreationError(true);

    setTimeout(() => {
      setOldCreationError(false);
    }, 4000);
  }

  async function handleDownloadCreation() {
    downloadCreation(creation, setLoadingDownload, showErrorMessage);
  }

  async function handleConvertCreation() {
    if (showConfirmationModal) {
      convertCreation(creation, setLoadingConvert, showErrorMessage, router);
    } else {
      setShowConfirmationModal(true);
    }
  }

  const confirmationModalContent = (
    <Box px={2}>
      <Typography>
        By converting it, a new version will be available for editing in a few seconds.
      </Typography>

      <Box display="flex" justifyContent="center" mt={4} gap={1}>
        <CustomButton
          style={{ minWidth: 180 }}
          variant="ctaPrimaryOutline"
          onClick={() => setShowConfirmationModal(false)}
          disabled={loadingDownload || loadingConvert}
        >
          Cancel
        </CustomButton>

        <CustomButton
          style={{ minWidth: 180 }}
          disabled={loadingDownload || loadingConvert}
          onClick={handleConvertCreation}
        >
          {loadingConvert ? (
            <>
              <CircularProgress size={14} color="inherit" style={{ marginRight: 5 }} />
              Converting
            </>
          ) : (
            'Convert Creation'
          )}
        </CustomButton>
      </Box>
    </Box>
  );

  const defaultModalContent = (
    <Box px={2} minHeight={150}>
      <Typography>
        This creation was made using an outdated version of the workspace. You can download a
        non-editable file as it is or edit it using new features.
      </Typography>

      <Box display="flex" justifyContent="center" mt={4} gap={2}>
        <CustomButton
          disabled={loadingDownload || loadingConvert}
          variant="ctaPrimaryOutline"
          onClick={handleDownloadCreation}
          style={{ minWidth: 180 }}
        >
          {loadingDownload ? (
            <>
              <CircularProgress size={14} color="inherit" style={{ marginRight: 5 }} />
              Downloading
            </>
          ) : (
            'Download File'
          )}
        </CustomButton>

        <CustomButton
          style={{ minWidth: 180 }}
          disabled={loadingDownload || loadingConvert}
          onClick={handleConvertCreation}
        >
          Edit My Creation
        </CustomButton>
      </Box>

      {oldCreationError && (
        <Box position="absolute" bottom={24} left={0} right={0}>
          <Typography variant="body2" style={{ color: '#FF0000' }}>
            An error occurred. Please try again later!
          </Typography>
        </Box>
      )}
    </Box>
  );

  const trialUserContent = (
    <Box px={2} minHeight={150}>
      <Typography>
        This creation was made using an outdated version of the workspace. Unfortunately, only
        subscribers can convert creations. Subscribe to a plan and edit it in the new workspace.
      </Typography>

      <Box display="flex" justifyContent="center" mt={4} gap={2}>
        <CustomButton style={{ minWidth: 180 }} variant="ctaPrimaryOutline" onClick={onClose}>
          Cancel
        </CustomButton>

        <CustomButton
          style={{ minWidth: 180 }}
          onClick={() => {
            setOferPlanModalOpen(true);
            onClose();
          }}
        >
          Subscribe to a Plan
        </CustomButton>
      </Box>
    </Box>
  );

  return (
    <>
      <BaseModal
        open={open}
        title={
          showConfirmationModal ? (
            <b>Are you sure you want to convert this creation?</b>
          ) : (
            <b>Old workspace version</b>
          )
        }
        content={
          user.tp_user === 'T'
            ? trialUserContent
            : showConfirmationModal
            ? confirmationModalContent
            : defaultModalContent
        }
        cancelButton={false}
        actionText="Got it!"
        onClose={onClose}
        actionButton={false}
      />

      <LimitationModal open={oferPlanModalOpen} onClose={() => setOferPlanModalOpen(false)} />
    </>
  );
}

OldCreationVersionModal.propTypes = Props;
